/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import PropTypes from 'prop-types';

import React, { Component } from 'react';

import {
	FlexLayout,
	FlexItem,
	Checkbox,
	List,
	ListItem,
	SVGIcon,
	Tooltip,
	listEventTypes,
	ProgressIndicator,
	Container,
} from '@waca/ca-ui-toolkit';
import coachMark16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/coach-mark_16.js';
import PanelMessage from 'common/components/PanelMessage';
import stringResource from 'ba-filter-nls';
import FilterListItem from './FilterListItem';

const { selectionEventTypes } = listEventTypes;

/*
 * FilterList implementation (leverages List.js)
 */
export default class FilterList extends Component<any, any> {
	static propTypes = {
		totalValues: PropTypes.number.isRequired,
		onClick: PropTypes.func.isRequired,
		getValue: PropTypes.func.isRequired,
		contentRenderer: PropTypes.func,
		initialIndex: PropTypes.number,
		selected: PropTypes.bool,
		showPath: PropTypes.bool,
		readOnly: PropTypes.bool,
		isSearching: PropTypes.bool,
	};

	static defaultProps = {
		showPath: false,
		readOnly: false,
		isSearching: false,
	};

	componentWillUpdate() {
		// always re-fetch data value from parent control.
		this._itemMap = new Map();
	}

	_itemMap = new Map();

	_getValue = index => {
		if (this._itemMap.has(index)) {
			return this._itemMap.get(index);
		}
		const item = this.props.getValue(index);

		if (item) {
			this._itemMap.set(index, item);

			return item;
		}

		return {};
	};

	_itemRenderer = (index, props) => {
		const { selected, ...other } = props;
		const { showPath, readOnly } = this.props;
		const item = this._getValue(index);
		let { caption, subcaption, selectable } = item;

		caption += '';
		subcaption += '';
		const { matchesCondition } = item;

		if (selectable === undefined) {
			selectable = true;
		}

		return (
			<ListItem
				length={this.props.totalValues}
				index={index}
				selected={!!selected}
				{...other}
				data-tid={`filterListItem${index}`}
				ariaSelected={!!selected}
				selectable={selectable}
			>
				{selectable && (
					<FlexLayout>
						<FlexItem gutter={[0, 1, 0, 0]}>
							<Checkbox checked={!!selected} visual readOnly={readOnly} />
						</FlexItem>
					</FlexLayout>
				)}
				<FilterListItem
					showPath={showPath}
					caption={caption}
					subcaption={subcaption}
					matchesCondition={matchesCondition}
				/>
				{matchesCondition === true && (
					<FlexItem gutter={0.5}>
						<Tooltip
							gutter={0.5}
							title={stringResource.get('reflectsFilterCondition')}
							aria-label={stringResource.get('reflectsFilterCondition')}
							orient="left"
							focusable
						>
							<SVGIcon
								iconId={coachMark16.id}
								intent="positive"
								width={8}
								height={8}
							/>
						</Tooltip>
					</FlexItem>
				)}
			</ListItem>
		);
	};

	_onSelectionChange = event => {
		if (this.props.readOnly) {
			return;
		}
		const eventType = event.type;

		switch (eventType) {
			case selectionEventTypes.SET:
			case selectionEventTypes.TOGGLE:
				this.props.onClick(this._getValue(event.index));
				break;
			default:
		}
	};

	_isSelected = index => !!this._getValue(index).selected;

	render() {
		const { initialIndex, totalValues, isSearching } = this.props;

		return (
			<Container height="100%">
				{isSearching ? (
					<PanelMessage>
						<ProgressIndicator size="normal" />
					</PanelMessage>
				) : (
					<List
						length={totalValues}
						itemRenderer={
							this.props.contentRenderer
								? this.props.contentRenderer
								: this._itemRenderer
						}
						initialIndex={initialIndex}
						onSelectionChange={this._onSelectionChange}
						toggleOnClick
						selectionMode="single"
						listType="uniform"
						isSelected={this._isSelected}
						getKey={v => v}
						ariaMultiselectable
					/>
				)}
			</Container>
		);
	}
}
